/* eslint-disable @typescript-eslint/no-explicit-any */
import { memo, useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Container, Stack, Typography } from '@mui/material';
import PositiveButton from 'components/cmn/PositiveButton';
import useInspection from 'hooks/useInspection';
import { useProject } from 'hooks/useProject';
import { IPostSurveyResultListProps } from 'interface/project/projectInfo';
import { useAppDispatch } from 'store';
import { closeBackdrop, openBackdrop } from 'store/backdrop/backdropSlice';
import { postInspectionResult, postInspectionClickLogs } from 'store/inspection/inspectionSlice';
import { showMessage } from 'store/message/messageSlice';
import { inspectionLogger } from 'utils/inspectionLogger';
import SurveyRadioBox from './survey/SurveyRadioBox';
import SurveyShortAnswer from './survey/SurveyShortAnswer';
import SurveyDescriptionAnswer from './survey/SurveyDescriptionAnswer';
import SurveyMultiChoice from './survey/SurveyMultiChoice';
import SurveyLK from './survey/SurveyLK';

function ClassificationAdditionalQuestion(): JSX.Element {
  const { surveyAdd, inspectionDetail, changeClassification, inspectionFinalBase } = useInspection();
  const { surveyResultList, progressInfo, urlPartCode, urlProjectId, changeProgressInfo, changeSurveyResult } =
    useProject();
  const [isComplete, setComplete] = useState(false);
  const [isDisabled, setIsDisabled] = useState(true);
  const dispatch = useAppDispatch();

  const handleNextButton = () => {
    changeProgressInfo(progressInfo + 1);
    if (progressInfo + 1 === surveyAdd.length) {
      setComplete(true);
    }
  };
  const handleNext = useCallback(
    async (_type: string, _surveyId: string, _info: any) => {
      let tmpResult: IPostSurveyResultListProps = {
        surveyId: '',
        answerResult: '',
        choiceResultList: [],
      };
      switch (_type) {
        case 'OC': // OC(oneChoice):객관식
        case 'LK': // LK: 리커트
          tmpResult = {
            surveyId: _surveyId,
            answerResult: '',
            choiceResultList: [_info],
          };
          break;
        case 'MC': // MC(multiChoice):체크박스,
          tmpResult = {
            surveyId: _surveyId,
            answerResult: '',
            choiceResultList: _info,
          };
          break;
        case 'DA': // DA(descriptiveAnswer):서술형
        case 'SA': // SA(shortAnswer):단답형
          tmpResult = {
            surveyId: _surveyId,
            answerResult: _info,
            choiceResultList: [],
          };
          break;
        default:
          break;
      }
      await changeSurveyResult(tmpResult);
    },
    [changeSurveyResult],
  );

  const handleResultInspectionSave = useCallback(async () => {
    // value > 0인 방에서만 항목을 수집하고, array가 존재하는지 확인
    const allIds = inspectionFinalBase
      .filter((obj: any) => obj.value > 0 && obj.array && Array.isArray(obj.array) && obj.array.length > 0)
      .flatMap((obj: any) => {
        return obj.array
          .map((obj2: any) => obj2?.id)
          .filter((id: any) => id != null && id !== undefined && id !== '');
      });
    
    dispatch(openBackdrop({}));
    
    // 클릭 로그 전송 (비동기로 처리)
    if (urlProjectId) {
      inspectionLogger.flushAll().then((pendingLogs) => {
        if (pendingLogs.length > 0) {
          dispatch(postInspectionClickLogs({ projectId: urlProjectId, logs: pendingLogs }))
            .unwrap()
            .then(() => {
              console.log(`✅ 클릭 로그 ${pendingLogs.length}개 최종 전송 완료`);
            })
            .catch((logError) => {
              console.error('❌ 클릭 로그 저장 실패:', logError);
              // 로그 저장 실패는 무시하고 계속 진행
            });
        }
      });
    }
    
    await dispatch(
      postInspectionResult({
        projectId: urlProjectId || '',
        partCode: urlPartCode || '',
        inspectionId: inspectionDetail.id,
        // TODO: 새로 만들 예정
        // value > 0인 방에서만 항목을 수집하고, array가 존재하는지 확인
        ipsativeIdResultList: allIds,
        surveyAddResultList: surveyResultList,
      }),
    )
      .unwrap()
      .then((response: { status: number; data: { code: string; message: string } }) => {
        const { status } = response;
        if (status === 200) {
          dispatch(showMessage({ message: response?.data?.message || '성공했습니다.', variant: 'success' }));
        } else {
          dispatch(showMessage({ message: response?.data?.message || '실패했습니다.', variant: 'warning' }));
        }
      })
      .catch((e: any) => console.log(e))
      .finally(() => {
        dispatch(closeBackdrop());
      });
  }, [dispatch, inspectionDetail.id, inspectionFinalBase, surveyResultList, urlPartCode, urlProjectId]);

  useEffect(() => {
    if (isComplete && surveyAdd.length === surveyResultList.length) {
      handleResultInspectionSave();
      changeClassification('complete');
      changeProgressInfo(0);
    }
  }, [
    changeClassification,
    changeProgressInfo,
    handleResultInspectionSave,
    isComplete,
    surveyAdd.length,
    surveyResultList.length,
  ]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
      }}
    >
      <Box
        sx={{
          p: 4,
          width: '100%',
          maxWidth: { xs: '100%', md: '1536px' },
          height: { xs: 'auto', md: '864px' },
        }}
      >
        <Container maxWidth="xl" sx={{ height: '100%' }}>
          <Stack
            direction="column"
            justifyContent="center"
            alignContent="center"
            alignItems="center"
            sx={{ height: '100%' }}
          >
            <Container maxWidth="lg" sx={{ p: 0, height: 'calc(100% - 84px)' }}>
              <Box sx={{ position: 'relative' }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'white',
                    width: '100%',
                    borderRadius: '12px',
                    boxShadow: '0px 0px 8px 0px #00000010',
                    border: '1px solid rgba(0, 0, 0, 0.1)',
                    flex: 1,
                  }}
                >
                  <Stack direction="column" spacing={2} sx={{ width: '100%', p: '24px' }}>
                    <Typography sx={{ fontSize: 16, fontWeight: 'bold' }} color="text.primary" gutterBottom>
                      {surveyAdd[progressInfo]?.question || ''}
                      <em style={{ color: 'red' }}> *</em>
                    </Typography>
                    {surveyAdd[progressInfo]?.questionType === 'OC' && (
                      <SurveyRadioBox
                        id={surveyAdd[progressInfo].id}
                        items={surveyAdd[progressInfo].items}
                        type={surveyAdd[progressInfo].questionType}
                        handleNext={handleNext}
                        setIsDisabled={setIsDisabled}
                        surveyType="surveyAdd"
                        handleNextButton={() => {}}
                      />
                    )}
                    {surveyAdd[progressInfo]?.questionType === 'SA' && (
                      <SurveyShortAnswer
                        id={surveyAdd[progressInfo].id}
                        type={surveyAdd[progressInfo].questionType}
                        handleNext={handleNext}
                        setIsDisabled={setIsDisabled}
                        surveyType="surveyAdd"
                      />
                    )}
                    {surveyAdd[progressInfo]?.questionType === 'DA' && (
                      <SurveyDescriptionAnswer
                        id={surveyAdd[progressInfo].id}
                        type={surveyAdd[progressInfo].questionType}
                        handleNext={handleNext}
                        setIsDisabled={setIsDisabled}
                        surveyType="surveyAdd"
                      />
                    )}
                    {surveyAdd[progressInfo]?.questionType === 'MC' && (
                      <SurveyMultiChoice
                        id={surveyAdd[progressInfo].id}
                        items={surveyAdd[progressInfo]?.items}
                        type={surveyAdd[progressInfo]?.questionType}
                        handleNext={handleNext}
                        setIsDisabled={setIsDisabled}
                      />
                    )}
                    {surveyAdd[progressInfo]?.questionType === 'LK' && (
                      <SurveyLK
                        id={surveyAdd[progressInfo].id}
                        items={surveyAdd[progressInfo].items}
                        type={surveyAdd[progressInfo].questionType}
                        handleNext={handleNext}
                        setIsDisabled={setIsDisabled}
                        surveyType="surveyAdd"
                        questionOrderOneBased={progressInfo + 1}
                        surveyQuestionTotal={surveyAdd.length}
                        handleNextButton={() => {}}
                      />
                    )}
                  </Stack>
                </Box>
              </Box>
            </Container>
          </Stack>
        </Container>
      </Box>
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          width: '100%',
          bgcolor: 'background.paper',
          boxShadow: '0px -2px 10px rgba(0, 0, 0, 0.1)',
          py: 2,
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <PositiveButton
          variant="contained"
          onClick={handleNextButton}
          sx={{ width: { xs: 130 }, height: 52, typography: 's14w500' }}
          title={surveyAdd.length === progressInfo + 1 ? '완료' : '다음'}
          disabled={isDisabled}
        />
      </Box>
    </Box>
  );
}

export default memo(ClassificationAdditionalQuestion);
